import type { IntlShape } from 'react-intl'

import {
  getNotificationDetail,
  getNotificationTypeLabel,
} from '@b-stock/fe-core/bstock-react'
import type { NotificationReadReceiptItemDto } from '@b-stock/notification-api-client'

import { appendClickSourceParams } from '@/helpers/notificationClickSource'

import type { NotificationOverlayNotification } from './types'

// Distinct from the tray's 'notification_tray' source so downstream
// attribution can tell which surface drove a given click-through.
const NOTIFICATION_OVERLAY_CLICK_SOURCE = 'notification_overlay'

// Placeholder pending real per-notification-type CTA copy (e.g. "Bid now"
// for an outbid notice, "Track shipment" for a shipping update) - no such
// mapping exists yet, unlike the title/detail copy below. A generic label is
// used rather than omitting the action entirely, since a banner with no way
// to act on it would defeat most of the point of a high-urgency overlay.
const GENERIC_ACTION_LABEL = 'View'

/**
 * Turns a raw notification read-receipt into the already-resolved-copy shape
 * `NotificationOverlay` expects - the overlay component itself has no
 * `notificationType` -> copy mapping of its own (see
 * `NotificationOverlayNotification`'s doc comment), so a caller resolves
 * title/message/action before handing a receipt to the overlay surface.
 *
 * `formatMessage` must come from an `IntlShape` whose message catalog
 * includes fe-core's own translations - see `getNotificationTypeLabel`'s own
 * doc comment for what happens if it doesn't.
 */
export const buildOverlayCandidate = (
  receipt: NotificationReadReceiptItemDto,
  formatMessage: IntlShape['formatMessage']
): NotificationOverlayNotification => {
  const { notificationType, jsonPayload, deepLink } = receipt.content

  const href = deepLink
    ? appendClickSourceParams(
        deepLink,
        notificationType,
        NOTIFICATION_OVERLAY_CLICK_SOURCE
      )
    : undefined

  return {
    id: receipt._id,
    notificationType,
    title: getNotificationTypeLabel(notificationType, formatMessage),
    message: getNotificationDetail(
      jsonPayload as Record<string, unknown> | undefined
    ),
    action: href ? { label: GENERIC_ACTION_LABEL, href } : undefined,
  }
}
